import type {
  NotificationActionStyle,
  NotificationActionType,
  NotificationStatus as SharedNotificationStatus,
} from "@shared/types";
import type { NotificationSource } from "../notifications";

export enum NotificationType {
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
  SUCCESS = "success",
}

// Re-export shared enum as the canonical NotificationStatus for web
export {
  NotificationActionStyle as ActionStyle,
  NotificationActionType as ActionType,
  NotificationStatus,
} from "@shared/types";

export interface RedirectConfig {
  url: string;
  open_in_new_tab?: boolean;
  close_notification?: boolean;
}

export interface ApiCallPayload {
  // Email operations
  message_ids?: string[];

  // Todo operations
  title?: string;
  description?: string;
  due_date?: string;
  priority?: "high" | "medium" | "low";
  project_id?: string;
  labels?: string[];
  completed?: boolean;

  // Calendar operations
  summary?: string;
  start?: { dateTime: string; timeZone?: string };
  end?: { dateTime: string; timeZone?: string };
  attendees?: Array<{ email: string; displayName?: string }>;
  location?: string;

  // Generic fields
  id?: string;
  ids?: string[];
  status?: string;
  filters?: Record<string, string | number | boolean>;
  page?: number;
  per_page?: number;

  // File operations
  filename?: string;
  content?: string;
  metadata?: Record<string, string | number | boolean>;
}

export interface ApiCallConfig {
  endpoint: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  payload?: ApiCallPayload;
  headers?: Record<string, string>;
  success_message?: string;
  error_message?: string;
  is_internal?: boolean;
}

export interface WorkflowParameters {
  // Entity identifiers
  user_id?: string;
  notification_id?: string;
  entity_id?: string;
  entity_type?: "todo" | "calendar" | "email" | "note";

  // Action parameters
  action?: string;
  delay_minutes?: number;
  conditions?: Array<{
    field: string;
    operator: "equals" | "contains" | "greater_than" | "less_than";
    value: string | number | boolean;
  }>;

  // Data context
  context?: {
    source?: string;
    trigger_event?: string;
    priority?: number;
    metadata?: Record<string, string | number | boolean>;
  };
}

export interface WorkflowConfig {
  workflow_id: string;
  parameters?: WorkflowParameters;
}

export interface ModalProps {
  // Base modal props
  open?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;

  // Entity data
  todoId?: string;
  emailId?: string;
  email_id?: string; // Alternative naming for email ID
  calendarEventId?: string;
  noteId?: string;

  // Action contexts
  mode?: "create" | "edit" | "view" | "delete";
  defaultValues?: Record<string, string | number | boolean>;
  actionId?: string; // Action ID for tracking which action triggered the modal
  notificationId?: string; // Notification ID for tracking which notification triggered the modal

  // Email-specific props for EmailPreviewModal
  subject?: string;
  body?: string;
  recipients?: string[];

  // Component-specific data
  todo?: {
    id: string;
    title: string;
    description?: string;
    priority: "high" | "medium" | "low";
    due_date?: string;
    project_id?: string;
  };

  email?: {
    id: string;
    subject: string;
    to: string[];
    body?: string;
    attachments?: Array<{ name: string; url: string }>;
  };

  calendar?: {
    id: string;
    title: string;
    start: string;
    end: string;
    description?: string;
    location?: string;
  };
}

export interface ModalConfig {
  component: string;
  props?: ModalProps;
}

export interface ActionConfig {
  redirect?: RedirectConfig;
  api_call?: ApiCallConfig;
  workflow?: WorkflowConfig;
  modal?: ModalConfig;
}

export interface NotificationAction {
  id: string;
  type: NotificationActionType;
  label: string;
  style?: NotificationActionStyle;
  config: ActionConfig;
  requires_confirmation?: boolean;
  confirmation_message?: string;
  icon?: string;
  disabled?: boolean;
  executed?: boolean;
  executed_at?: string; // ISO string
}

export interface RichContent {
  // Interactive elements
  buttons?: Array<{
    label: string;
    action: "redirect" | "api_call" | "modal";
    style?: "primary" | "secondary" | "danger";
    config?: ActionConfig;
  }>;

  // Media content
  images?: Array<{
    url: string;
    alt?: string;
    caption?: string;
  }>;

  // Structured data
  charts?: Array<{
    type: "bar" | "line" | "pie" | "scatter";
    title: string;
    data: Array<{
      label: string;
      value: number;
      group?: string;
    }>;
  }>;

  // Lists and tables
  lists?: Array<{
    title?: string;
    items: string[];
    ordered?: boolean;
  }>;

  tables?: Array<{
    headers: string[];
    rows: string[][];
  }>;

  // Embedded content
  embeds?: Array<{
    type: "calendar" | "todo" | "document";
    data: Record<string, string | number | boolean>;
  }>;
}

export interface NotificationContent {
  title: string;
  body: string;
  actions?: NotificationAction[];
  rich_content?: RichContent;
}

export interface NotificationMetadata {
  // Source tracking
  source?: "system" | "user" | "integration" | "workflow";
  source_id?: string;

  // Entity relationships
  reminder_id?: string;
  todo_id?: string;
  calendar_event_id?: string;
  email_id?: string;
  project_id?: string;

  // Context information
  trigger_event?: string;
  user_action?: string;
  integration_name?: string;

  // Delivery tracking
  delivery_attempts?: number;
  last_attempt_at?: string;
  failure_reason?: string;

  // Grouping and categorization
  category?: string;
  tags?: string[];
  group_key?: string;

  // Custom tracking
  analytics?: {
    campaign_id?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  };

  // Timestamps
  created_at?: string;
  updated_at?: string;

  // Allow for additional custom fields
  [key: string]: string | number | boolean | string[] | object | undefined;
}

export interface ChannelDeliveryStatus {
  channel_type: string;
  status: SharedNotificationStatus;
  delivered_at?: string;
  error_message?: string;
  retry_count?: number;
}

export interface NotificationRecord {
  id: string;
  user_id: string;
  status: SharedNotificationStatus;
  type: NotificationType;
  created_at: string;
  delivered_at?: string;
  read_at?: string;
  source: NotificationSource;
  content: NotificationContent;
  metadata?: NotificationMetadata;
  channels: ChannelDeliveryStatus[];
}

export interface ActionResultData {
  // Entity results
  created_entity?: {
    id: string;
    type: string;
    title?: string;
    url?: string;
  };

  updated_entity?: {
    id: string;
    field: string;
    old_value: string | number | boolean;
    new_value: string | number | boolean;
  };

  // Operation results
  affected_count?: number;
  processed_items?: string[];
  skipped_items?: Array<{
    id: string;
    reason: string;
  }>;

  // Redirect information
  redirect_url?: string;
  redirect_delay_ms?: number;

  // Follow-up actions
  suggested_actions?: Array<{
    label: string;
    action_id: string;
    description?: string;
  }>;

  // Status information
  status?: "completed" | "pending" | "failed" | "partial";
  progress?: {
    current: number;
    total: number;
    percentage: number;
  };
}

export interface NotificationUpdate {
  // Status changes
  status?: SharedNotificationStatus;
  read_at?: string;
  archived_at?: string;
  snoozed_until?: string;

  // Content updates
  content?: Partial<NotificationContent>;

  // Action updates
  disable_actions?: string[]; // Action IDs to disable
  add_actions?: NotificationAction[];

  // Visual updates
  highlight?: boolean;
  badge_count?: number;

  // Metadata updates
  metadata?: Partial<NotificationMetadata>;

  // Expiration
  expires_at?: string;
  auto_archive_after?: number; // minutes
}

export interface ActionResult {
  success: boolean;
  message?: string;
  data?: ActionResultData;
  next_actions?: NotificationAction[];
  update_notification?: NotificationUpdate;
  error_code?: string;
}

export enum BulkActions {
  MARK_READ = "mark_read",
  ARCHIVE = "archive",
  DELETE = "delete",
}

// Streamed by the send_notification agent tool — rendered as a chat tool card
export interface SendNotificationData {
  success: boolean;
  notification_id: string;
  title: string;
  message: string;
  notification_type: string;
  status: string;
  delivered_channels: string[];
}

// API Request/Response types

export interface BulkActionRequest {
  notification_ids: string[];
  action: BulkActions;
}

export interface SnoozeRequest {
  snooze_until: string;
}

export interface NotificationResponse {
  success: boolean;
  message: string;
  data?: ActionResultData | NotificationRecord; // Allow both types
}

export interface PaginatedNotificationsResponse {
  notifications: NotificationRecord[];
  total: number;
  limit: number;
  offset: number;
}

// Hook options
export interface UseNotificationsOptions {
  status?: SharedNotificationStatus;
  limit?: number;
  offset?: number;
  channel_type?: string;
}
